import React, { useEffect, useMemo, useState } from 'react'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Stack from 'components_new/atoms/Stack'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'
import GenericListItem from 'components_new/molecules/GenericListItem'
import SimpleTable from 'components_new/molecules/SimpleTable'
import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import PageLayout from 'components_new/templates/PageLayout'
import BannerModal from 'components_new/templates/internal/StatusBanner/BannerModal'

import { useInternalStatusBannerActions } from 'redux/actions/internal/StatusBanner'
import { useInternalStatusBanners } from 'redux/hooks/internal/StatusBanner'
import { useInternalCustomers } from 'redux/hooks/internal/Customers'

const StatusBannerPage = () => {
  const { data, fetched } = useInternalStatusBanners()

  const { customerOptions, customersById } = useInternalCustomers()

  const {
    tryGetInternalStatusBanner,
    tryCreateStatusBanner,
    tryUpdateStatusBanner,
    tryDeleteStatusBanner
  } = useInternalStatusBannerActions()

  const [open, setOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)

  useEffect(() => {
    if (!fetched) {
      tryGetInternalStatusBanner()
    }
  }, [])

  const sortedBanners = useMemo(
    () =>
      Object.values(data).sort((a, b) => {
        if (a.visible !== b.visible) {
          return a.visible ? -1 : 1
        }

        return a.title.localeCompare(b.title, 'sv')
      }),
    [data]
  )

  const bannerRows = sortedBanners.map((banner) => ({
    id: banner.id,
    cells: [
      <GenericListItem
        key="banner"
        component="div"
        disablePadding={true}
        title={banner.title}
        body={banner.description}
        BodyProps={{ noWrap: false }}
      />,
      <Text key="level" variant="body2">
        {banner.level === 'important' ? 'Viktigt' : 'Info'}
      </Text>,
      <Text key="customers" variant="body2">
        {banner.customer_ids.length === 0
          ? 'Alla'
          : banner.customer_ids
            .map(
              (customerId) => customersById[customerId]?.name ?? 'Okänd kund'
            )
            .join(', ')}
      </Text>,
      <Text key="status" variant="body2">
        {banner.visible ? 'Aktiv' : 'Inaktiv'}
      </Text>,
      <Stack
        key="actions"
        direction="row"
        sx={{ gap: 0.5, justifyContent: 'flex-end' }}
      >
        <Tooltip title="Ändra">
          <IconButton
            onClick={() => {
              setSelected(banner.id)
              setOpen(true)
            }}
          >
            <Icon name="Edit" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ta bort">
          <IconButton onClick={() => setDeleteId(banner.id)}>
            <Icon name="Delete" />
          </IconButton>
        </Tooltip>
      </Stack>
    ]
  }))

  return (
    <PageLayout
      content={{ disablePadding: true, fullWidth: true }}
      variant="static"
      topbar={{
        breadcrumbs: [{ title: 'Admin' }, { title: 'Banner' }],
        actions: (
          <Button
            startIcon={<Icon name="Add" />}
            onClick={() => setOpen(true)}
            variant="text"
          >
            Skapa
          </Button>
        )
      }}
    >
      <Box sx={{ p: 4 }}>
        <SimpleTable
          hover={false}
          loading={!fetched}
          columns={[
            { title: 'Banner' },
            { title: 'Nivå', width: 110 },
            { title: 'Kunder', width: 220 },
            { title: 'Status', width: 110 },
            { title: '', width: 110, align: 'right' }
          ]}
          rows={bannerRows}
        />
      </Box>
      <BannerModal
        customerOptions={customerOptions}
        data={selected ? data[selected] : undefined}
        onClose={() => {
          setOpen(false)
          setSelected(null)
        }}
        open={open}
        submit={(body) => {
          selected
            ? tryUpdateStatusBanner(selected, body)
            : tryCreateStatusBanner(body)
          setOpen(false)
          setSelected(null)
        }}
      />
      <DeleteDialog
        open={!!deleteId}
        onClose={() => {
          setDeleteId(null)
        }}
        onDelete={(callback) => {
          if (deleteId) {
            tryDeleteStatusBanner(deleteId)
            setDeleteId(null)
          }

          callback()
        }}
        type="banner"
        title={deleteId ? data[deleteId].title : undefined}
      />
    </PageLayout>
  )
}

export default StatusBannerPage
